/**
 * Edição de configuração de sistema.
 *
 * Salvar/Remover no topo (padrão do painel). Para o SMTP, o valor é
 * editado em CAMPOS amigáveis (servidor, porta, SSL, usuário e senha
 * mascarada) no estilo dos plugins de e-mail do WordPress — os campos
 * apontam para value.host, value.user etc. (notação de ponto), então
 * chaves extras que existirem no JSON são preservadas no salvar.
 * Configuração que não seja o SMTP segue no editor de JSON de sempre.
 *
 * Avisos honestos: o servidor procura exatamente o nome "SMTP"
 * (renomear desliga o envio) e o salvar recarrega o transporte na
 * hora, sem reiniciar; "secure" é liga/desliga (SSL) — o backend não
 * tem as variações TLS/Nenhum de plugins do WordPress.
 */
import { JsonInput } from "react-admin-json-view";
import {
  EditContextProvider,
  required,
  SaveButton,
  SimpleForm,
  TextInput,
  useEditController,
} from "react-admin";
import { AppDeleteButton } from "../../components/AppDeleteButton";
import { useParams } from "react-router-dom";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import Grid from "@mui/material/Grid";
import { PageTopBreadcrumb } from "../../components/PageTopBreadcrumb";
import { determineUserReadableRole } from "../users/UserUtils";
import {
  isPlainObject,
  settingMeta,
  SmtpValueInputs,
} from "./system-settings-util";

export const SystemSettingEdit = (props: any) => {
  const { id } = useParams();
  const controller = useEditController({
    resource: "system-settings/crud",
    mutationMode: "pessimistic",
    id,
  });
  const isAdmin =
    determineUserReadableRole(localStorage.getItem("roles")) === "admin";
  const meta = settingMeta(controller?.record?.name);
  const isSmtpName =
    String(controller?.record?.name).toUpperCase() === "SMTP";
  // Campos padrão sempre visíveis (decisão do cliente — o SMTP é a
  // única configuração consumida). O editor JSON fica só como rede de
  // segurança para um valor gravado fora do formato de objeto simples
  // (hoje não existe nenhum).
  const exoticValue =
    controller?.record &&
    controller.record.value !== undefined &&
    controller.record.value !== null &&
    !isPlainObject(controller.record.value);

  const TopActions = () => (
    <>
      {isAdmin && <AppDeleteButton label="Remover" />}
      <SaveButton type="button" label="Salvar" />
    </>
  );

  return (
    <EditContextProvider value={controller}>
      {/* toolbar={false}: Salvar/Remover saem do rodapé e vão para o topo */}
      <SimpleForm toolbar={false}>
        <PageTopBreadcrumb
          title="Configurações de Sistema"
          firstPath="/#/system-settings/crud"
          secondTitle="Editando"
          thirdTitle={controller && controller.record && controller.record.name}
          actionType="show"
          actions={<TopActions />}
        />
        <Card sx={{ padding: 2, marginBottom: 3, width: "100%" }}>
          <CardHeader
            title={
              isSmtpName ? "Servidor de E-mail (SMTP)" : "Dados da Configuração"
            }
          />
          <CardContent>
            <Grid container columnSpacing={2} rowSpacing={0.5}>
              <Grid item xs={12} md={6}>
                {/* Chave do registro: protegida contra edição acidental */}
                <TextInput
                  source="id"
                  label="ID"
                  fullWidth
                  helperText={false}
                  disabled
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextInput
                  source="name"
                  label="Nome"
                  fullWidth
                  validate={required()}
                  helperText={meta.renameWarning ?? false}
                />
              </Grid>
              {exoticValue ? (
                <Grid item xs={12}>
                  <JsonInput source="value" label="Valor" />
                </Grid>
              ) : (
                <>
                  <SmtpValueInputs />
                  {isSmtpName && (
                    <Grid item xs={12}>
                      <CardHeader
                        sx={{ padding: "6px 0 0" }}
                        subheader="A alteração vale imediatamente para os próximos e-mails — o servidor recarrega o transporte sozinho, sem reiniciar."
                        subheaderTypographyProps={{ fontSize: 12.5 }}
                      />
                    </Grid>
                  )}
                </>
              )}
            </Grid>
          </CardContent>
        </Card>
      </SimpleForm>
    </EditContextProvider>
  );
};
